import { CommonModule } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import * as bootstrap from 'bootstrap';
import { OrganizationService } from './services/organization.service';
import { FormsModule } from '@angular/forms';
import { DepartmentService } from './services/department.service';
import { filter, ReplaySubject, Subject, Subscription, takeUntil } from 'rxjs';
import { LoadingSpinnerComponent } from "./helpers/loading-spinner/loading-spinner.component";
import { ServiceAreaService } from './services/service-area.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { TenantService } from './services/tenant.service';
import { ToastsComponent } from "./components/shared/toasts/toasts.component";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, RouterModule, FormsModule, LoadingSpinnerComponent, ToastsComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'SMART Engage Hub';

  private readonly _destroying$ = new Subject<void>();
  private msalInitialized$ = new ReplaySubject<void>(1);

  selectedOrganizationId: any;
  isDepartmentEntryComplete: boolean = false;

  workflowSteps: { title: string, completed: boolean }[] = [];
  serviceAreas: any[] = [];
  organizations: any[] = [];

  userName: any;
  isLoggedIn: any;

  constructor(
    private organizationService: OrganizationService,
    private departmentService: DepartmentService,
    private serviceAreaService: ServiceAreaService,
    private msalBroadcastService: MsalBroadcastService,
    private authService: MsalService,
    private tenantService: TenantService,
    private zone: NgZone
  ) { }

  ngOnInit(): void {

    this.msalInitialization();


    this.msalInitialized$.pipe(
      filter(() => this.authService.instance.getAllAccounts().length > 0),
      takeUntil(this._destroying$)
    ).subscribe(() => {
      this.loadinitialData();
      console.log('MSAL Initialized');
    });

    // Initialize the offcanvas element

    // Check settings to determine if offcanvas should be shown on startup
    // const showWorkflowPanelOnStart = false; // Replace with your actual setting
    // const offcanvasElement = document.getElementById('workflowOffcanvas');
    // if (showWorkflowPanelOnStart && offcanvasElement) {  // Check for null
    //   const offcanvas = new bootstrap.Offcanvas(offcanvasElement);
    //   offcanvas.show();
    // }    

    // Subscribe to changes in organization ID (dropdown selection)

    // Subscribe to service area changes
    this.serviceAreaService.serviceAreasChanged$
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        this.loadServiceAreas(); // Reload service areas when changed
      });

    // Subscribe to the isDepartmentEntryComplete flag
    this.organizationService.isDepartmentEntryComplete$
      .pipe(takeUntil(this._destroying$))
      .subscribe(value => {
        this.isDepartmentEntryComplete = value;
      });

  }

  loadinitialData() {


    this.loadServiceAreas();
    this.loadWorkflowSteps();

    this.loadOrganizations();

    this.organizationService.organizationId$
      .pipe(takeUntil(this._destroying$))
      .subscribe((orgId) => {
        this.selectedOrganizationId = orgId;
        // Reload data when organization changes
        this.loadWorkflowSteps();
        this.loadServiceAreas();
      });


  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  //#region Msal Configuration

  private msalInitialization() {


    this.authService.handleRedirectObservable().subscribe({
      next: (result) => {
        console.log('Redirect handled successfully:', result);
        // Force immediate account selection after successful redirect
        if (result) {
          const accounts = this.authService.instance.getAllAccounts();
          if (accounts.length > 0) {
            this.authService.instance.setActiveAccount(accounts[0]);
          }
        }
        this.updateUserStatus();
      },
      error: (error) => {
        console.error('Redirect error:', error);
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.msalInitialized$.next();
        this.updateUserStatus();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {

        if (result.eventType === EventType.LOGIN_SUCCESS) {
          console.debug('Login Success:', result);
        }

        this.updateUserStatus();
      });

    this.updateUserStatus();
  }

  //#endregion

  //#region Authentication

  private updateUserStatus(): void {
    const accounts: AccountInfo[] = this.authService.instance.getAllAccounts();

    if (accounts.length > 0) {
      const account = accounts[0];
      this.isLoggedIn = true;
      this.userName = account.name || account.username; // Display name or username

      // Extract tenant ID and check if it matches allowed tenants
      //const tenantId = account.idTokenClaims?.tid;
      // 01583be4-f096-49f4-bd19-56b649a8482f - ICS.AI Corporate Directory
      // 7dbd3d8d-379e-4afd-b0f5-cdca8ef69e51 - ICS Technical Team Directory
      //const allowedTenants = ['01583be4-f096-49f4-bd19-56b649a8482f', '7dbd3d8d-379e-4afd-b0f5-cdca8ef69e51']; // Replace with actual tenant IDs
      //this.isAllowedTenant = allowedTenants.includes(tenantId || '');

    } else {
      this.isLoggedIn = false;
      this.userName = null;
    }
  }

  login(): void {
    this.authService.loginRedirect();
  }

  logout() {
    this.organizationService.setOrganizationId(null); // Clear organizationId
    this.authService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }

  //#endregion


  onOrganizationChange(event: any) {
    const selectedOrgId = event.target.value;
    this.organizationService.setOrganizationId(selectedOrgId);
    console.debug('AppComponent OnChange: Current organization ID:', selectedOrgId);
  }

  loadServiceAreas() {
    this.serviceAreaService.getServiceAreas().subscribe(serviceAreas => {
      this.serviceAreas = serviceAreas.sort((a: any, b: any) => a.name.localeCompare(b.name));
    })
  }

  loadWorkflowSteps() {
    this.departmentService.getDepartments().subscribe(departments => {
      this.workflowSteps = departments.map((dept: { name: any; }) => {
        return {
          title: dept.name,
          completed: false
        };
      });
    });
  }

  loadOrganizations() {
    this.organizationService.getOrganizations().subscribe({
      next: (data) => {
        if (data) {
          this.organizations = data;
        } else {
          this.organizations = [];
        }
      },
      error: (error) => {
        console.error('Error loading organizations:', error);
        this.organizations = [];
      }
    });
  }

}
